// src/hooks/useAuth.js
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useEffect, useState } from 'react';

/**
 * Custom hook for Firebase authentication + role management
 * Returns: { user, role, loading, error }
 * 
 * Role fallback: 'student' → 'teacher' → 'partner' (if doc exists but role missing)
 * Error safe: never crashes, logs to console for debugging
 */
export function useAuth() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (currentUser) {
          // Fetch user role from Firestore `/users/{uid}`
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          let resolvedRole = 'student'; // default fallback

          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Prioritize explicit role, then infer from doc presence
            if (userData.role) {
              resolvedRole = userData.role;
            } else if (userData.stripeConnectId) {
              resolvedRole = 'partner';
            } else if (userData.createdAt) {
              resolvedRole = 'student';
            }
          } else {
            // User auth exists but no Firestore doc → create minimal one
            console.warn(`User ${currentUser.uid} has no Firestore doc — creating minimal profile`);
            // (Optional: auto-create doc here if desired)
          }

          setUser(currentUser);
          setRole(resolvedRole);
          setError(null);
        } else {
          // No user signed in
          setUser(null);
          setRole(null);
          setError(null);
        }
      } catch (err) {
        console.error('Auth hook error:', err);
        setError('Failed to load user profile. Please refresh.');
        setUser(null);
        setRole(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return { user, role, loading, error };
}